import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, X } from "lucide-react";
import HeadlessDialog from './Dialog/HeadlessDialog';
import { contactApi } from '@/api';
import { useToast } from '@/hooks/use-toast';

interface MessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const MessageDialog = ({ isOpen, onClose }: MessageDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    category: "",
    message: ""
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.category || !formData.message) {
      toast({
        title: "提交失败",
        description: "请填写必填字段：姓名、邮箱、咨询类型和详细描述",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await contactApi.submitContactForm({
        name: formData.name,
        company: formData.company || undefined,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject || undefined,
        category: formData.category,
        message: formData.message
      });
      
      if (response.code === 200) {
        setIsSubmitted(true);
        
        toast({
          title: "提交成功",
          description: response.msg || "我们已收到您的消息，将在24小时内与您联系。",
        });
      } else {
        throw new Error(response.msg || "提交失败");
      }
    } catch (error) {
      console.error('提交联系表单失败:', error);
      
      toast({
        title: "提交失败",
        description: "消息提交失败，请稍后重试",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      subject: "",
      category: "",
      message: ""
    });
    setIsSubmitted(false);
  };

  const handleClose = () => {
    if (!isSubmitted) {
      resetForm();
      onClose();
    }
  };

  if (isSubmitted) {
    return (
      <HeadlessDialog
        isOpen={isOpen}
        onClose={handleClose}
        title="提交成功！"
        description="我们已收到您的消息，将在24小时内与您联系。"
        size="sm"
      >
        <div className="text-center py-6">
          <Button 
            onClick={() => {
              setIsSubmitted(false);
              resetForm();
              onClose();
            }}
            className="w-full"
          >
            确定
          </Button>
        </div>
      </HeadlessDialog>
    );
  }

  return (
    <HeadlessDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="发送消息"
      description="请填写以下信息，我们会尽快与您联系"
      size="lg"
      isShowClose={false}
    >
      <div className="relative">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-0 bottom-10 text-gray-400 hover:text-gray-600 focus:outline-none"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">姓名 *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="请输入您的姓名"
              required
            />
          </div>
          <div>
            <Label htmlFor="company">公司/学校</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange("company", e.target.value)}
              placeholder="请输入公司或学校名称"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">邮箱 *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="请输入邮箱地址"
              required
            />
          </div>
          <div>
            <Label htmlFor="phone">电话</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="请输入联系电话"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">咨询类型 *</Label>
            <Select value={formData.category} onValueChange={(value) => handleInputChange("category", value)}>
              <SelectTrigger>
                <SelectValue placeholder="请选择咨询类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technical">技术支持</SelectItem>
                <SelectItem value="sales">商务合作</SelectItem>
                {/* <SelectItem value="training">培训服务</SelectItem> */}
                <SelectItem value="feedback">意见反馈</SelectItem>
                <SelectItem value="other">其他</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="subject">主题</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleInputChange("subject", e.target.value)}
              placeholder="请输入消息主题"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="message">详细描述 *</Label>
          <Textarea
            id="message"
            value={formData.message}
            onChange={(e) => handleInputChange("message", e.target.value)}
            placeholder="请详细描述您的问题或需求..."
            rows={6}
            required
          />
        </div>

        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
          {isSubmitting ? (
            "提交中..."
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              发送消息
            </>
          )}
        </Button>
      </form>
    </HeadlessDialog>
  );
};

export default MessageDialog;